import { GoalTreeIndex, ID, InheritColors, LifeArea } from '@griply/types';

type GetLinkedItemColorOptions = {
  goals: {
    id: ID;
    parentIds?: ID[] | null;
    lifeAreaId?: ID | null;
    color?: string | null;
  }[];
  lifeAreas: LifeArea[];
  usedIds?: Set<ID>;
  index?: GoalTreeIndex;
};

const resolveLinkedItemColor = (
  linkedItemId: ID,
  { lifeAreas, goals, usedIds, index }: GetLinkedItemColorOptions,
): string | null => {
  const _usedIds = usedIds ?? new Set();
  // to prevent loops we cancel out when revisiting a used ID
  if (_usedIds.has(linkedItemId)) {
    return null;
  }
  _usedIds.add(linkedItemId);

  const goal = index
    ? index.goalById.get(linkedItemId)
    : goals.find(({ id }) => id === linkedItemId);
  const lifeArea = goal
    ? undefined
    : lifeAreas.find(({ id }) => id === linkedItemId);

  const parentId = goal?.parentIds?.[goal.parentIds.length - 1];
  const shouldInheritFromParent =
    goal?.color === InheritColors.Goal || !goal?.color;

  if (shouldInheritFromParent && parentId) {
    return getLinkedItemColor(parentId, {
      lifeAreas,
      goals,
      usedIds: _usedIds,
      index,
    });
  }

  const shouldInheritFromLifeArea =
    goal?.color === InheritColors.LifeArea ||
    goal?.color === InheritColors.Goal ||
    !goal?.color;

  if (shouldInheritFromLifeArea && goal?.lifeAreaId) {
    const lifeArea = lifeAreas.find(({ id }) => id === goal.lifeAreaId);
    return lifeArea?.color ?? null;
  }

  if (
    goal?.color &&
    goal.color !== InheritColors.Goal &&
    goal.color !== InheritColors.LifeArea
  ) {
    return goal.color;
  }

  return lifeArea?.color ?? null;
};

export const getLinkedItemColor = (
  linkedItemId: ID,
  options: GetLinkedItemColorOptions,
): string | null => {
  const { index, usedIds } = options;

  // A mid-recursion result depends on the entry point's usedIds, so only top-level calls cache.
  if (!index || usedIds) {
    return resolveLinkedItemColor(linkedItemId, options);
  }

  const cached = index.colorByLinkedId.get(linkedItemId);
  if (cached !== undefined) {
    return cached;
  }

  const color = resolveLinkedItemColor(linkedItemId, options);
  index.colorByLinkedId.set(linkedItemId, color);
  return color;
};
